import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import incrementUnique from 'data/lib/increment-unique'
import { diff_obj } from 'data/lib/diff'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"
import propertyService from "./property"

type Recovery = {
  propertyid: number
  recoveryid: number
}
// private module logic //

const id: string = "recovery"
const initialState: ApiResult<Recovery[]> = { id, state: "init", value: [] }
const recoveryStream = new BehaviorSubject<ApiResult<Recovery[]>>(initialState)
const recoveryEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let recoveryMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  recoveryStream.next({ id, state: 'ok', value: [] })
  recoveryMap = {}
}

function get(recoveryid: number): Recovery {
  return recoveryMap[recoveryid]
}

function copy(propertyid:number, recoveryid:number, code:string) {
  const currentRecoveryVal = recoveryStream.getValue().value;
  recoveryStream.next({ id, state: 'busy', value: currentRecoveryVal });

  const payload = {table: 'recovery', recordid: recoveryid, _synchronous: true, code: incrementUnique(code, "code", currentRecoveryVal)};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((recoveries: Recovery[]) => {
      const newRecord = recoveries[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.recoveryid),
        redofunc: () => copy(propertyid, recoveryid, code)
      });
      recoveryMap[newRecord.recoveryid] = newRecord
      // merge results unique by recoveryid
      const currentRecoveryVal = recoveryStream.getValue().value
      recoveryStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentRecoveryVal, 'recoveryid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryEventStream.next({ type: 'RECOVERY_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryStream.next({ id, state: 'error', error: err, value: currentRecoveryVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveries(propertyid)
      return err
    })
}

function copies(propertyid: number, recoveryid: number, count: number) {
  const currentRecoveryVal = recoveryStream.getValue().value;
  recoveryStream.next({ id, state: 'busy', value: currentRecoveryVal });

  const payload = {table: 'recovery', recordid: recoveryid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((recoveries: Recovery[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(recoveries, 'recoveryid')),
        redofunc: () => copies(propertyid, recoveryid, count)
      });
      _.forEach(recoveries, newRecord => recoveryMap[newRecord.recoveryid] = newRecord)
      // merge results unique by recoveryid
      const currentRecoveryVal = recoveryStream.getValue().value
      recoveryStream.next({
        id,
        state: 'ok',
        value: _.unionBy(recoveries, currentRecoveryVal, 'recoveryid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryEventStream.next({ type: 'RECOVERY_COPIED', payload: recoveries })
      return recoveries
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryStream.next({ id, state: 'error', error: err, value: currentRecoveryVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveries(propertyid)
      return err
    })
}

function create(propertyid: number): Promise<Recovery | { message: string }> {
  const currentValue = recoveryStream.getValue().value
  recoveryStream.next({ id, state: 'busy', value: currentValue })
  const property = propertyService.get();

  const payload = {
    propertyid,
    recoverydetail: {
      areaMeasureNamedItemId: property['defaultsnameditem']['namedRecoveryProRataProRataDenominator']['areaNamedItemId'],
      cap: {},
      proRataPercent: null,
      proRataType: property['defaultsnameditem']['namedRecoveryProRataProRataType'],
      recoveryPoolNamedItemId: "",
      reportTagNamedItemId: "",
      stop: "",
      stopType: property['defaultsnameditem']['namedRecoveryProRataProRataStopType'],
      state: "proRataRecovery",
    }
  }

  return Client.post(`recovery`, payload).then((recoveries: Recovery) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, recoveries.recoveryid),
      redofunc: () => create(propertyid)
    })
    recoveryMap[recoveries.recoveryid] = recoveries
    // merge results unique by recoveryid
    const currentValue = recoveryStream.getValue().value
    recoveryStream.next({
      id,
      state: 'ok',
      value: _.unionBy([recoveries], currentValue, 'recoveryid')
    })
    recoveryEventStream.next({ type: 'RECOVERY_CREATED', payload: recoveries })
    return recoveries
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    recoveryStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchRecoveries(propertyid)
    return err
  })
}

async function deleteOne(
  propertyid: number,
  recoveryid: number
): Promise<boolean | { message: string }> {
  const currentValue = recoveryStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, recoveryid)
  const refreshFn = () => fetchRecoveries(propertyid)
  if (!(await handleReferences('recovery', recoveryid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoveryid
  const value = _(currentValue)
    .filter((p) => p.recoveryid !== recoveryid)
    .value()
  value.forEach((p) => (recoveryMap[p.recoveryid] = p))

  recoveryStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoveryid).then(() =>
  Client.del(`recovery?recoveryid=eq.${recoveryid}`)
    .then((recoveries: Recovery[]) => {
      recoveries.forEach(r => delete recoveryMap[r.recoveryid])
      push_undo({
        undofunc: () => {
          Client.post('recoveries', recoveries)
          recoveries.forEach(r => recoveryMap[r.recoveryid] = r)
          recoveryStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, recoveryid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveries(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  recoveryids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = recoveryStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchRecoveries(propertyid)
  if (!(await handleReferences('recovery', recoveryids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoveryids
  const value = _(currentValue)
    .filter((p) => !recoveryids.includes(p.recoveryid))
    .value()
  value.forEach((p) => (recoveryMap[p.recoveryid] = p))

  recoveryStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoveryids).then(() =>
  Client.del(`recovery?recoveryid=in.${recoveryids}`)
    .then((recoveries: Recovery[]) => {
      recoveries.forEach(r => {delete recoveryMap[r.recoveryid]})
      push_undo({
        undofunc: () => {
          Client.post('recoveries', recoveries)
          recoveries.forEach(r => recoveryMap[r.recoveryid] = r)
          recoveryStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, recoveryids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveries(propertyid)
      return err
    })
  )
}

function fetchRecoveries(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Recovery[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  recoveryStream.next({ id, state: 'busy', value: recoveryStream.getValue().value })

  let url = `recovery?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `recovery?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Recovery[]) => {
    _.forEach(records, (recoveries) => (recoveryMap[recoveries.recoveryid] = recoveries))
    // merge results unique by recoveryid
    recoveryStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function patch(recovery: Recovery): Promise<Recovery | { message: string }> {
  const currentValue = recoveryStream.getValue().value
  const recoveryid: number = recovery.recoveryid
  const currentRecoveries = _.find(currentValue, { recoveryid })
  const changeset = diff_obj(recovery, currentRecoveries)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentRecoveries),
    redofunc: () => patch(recovery)
  })

  // optimistic update
  const value = _.cloneDeep(currentValue)
  value.splice(_.findIndex(currentValue, a => a.recoveryid === recoveryid), 1, recovery)
  recoveryStream.next({
    id,
    state: 'ok',
    value
  })

  return _patches_.watch(recoveryid, Client.patch(`recovery?recoveryid=eq.${recoveryid}`, changeset)
    .then((recoveries: Recovery[]) => {
      _.forEach(recoveries, (recoveries) => (recoveryMap[recoveries.recoveryid] = recoveries))
      return recoveries[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentRecoveries, 'propertyid')
      if (propertyid) fetchRecoveries(propertyid)
      return err
    }))
}

// public module interface //

const recoveryService = {
  stream: () => recoveryStream.asObservable(),
  value: () => recoveryStream.getValue().value,
  eventStream: () => recoveryEventStream.asObservable(),
  copy,
  copies,
  clear,
  create,
  deleteMultiple,
  deleteOne,
  get,
  fetchRecoveries,
  patch
}

export default recoveryService
